import { useSelector } from 'react-redux'
import { FileOutlined, PaperClipOutlined } from '@ant-design/icons'
import { Image } from 'antd'

import { fDateTime, isValidArray } from '../../../../../../utils/helpers'
import { testRunState } from '../../../../../../redux/testRun/testRunList.slice'
import SkeletonCommentHistory from './SkeletonCommentHistory'
import styles from '../../../../../../testrun.module.css'

function isImage(url) {
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url)
}

function AttachmentList({ attachments = [] }) {
    if (!isValidArray(attachments)) return null

    return (
        <div className={styles.commentAttachments}>
            <div className={styles.commentAttachmentsLabel}>
                <PaperClipOutlined />
                <span>
                    {attachments.length} Attachment
                    {attachments.length !== 1 ? 's' : ''}
                </span>
            </div>
            <div className={styles.commentAttachmentsGrid}>
                {attachments.map((att) =>
                    isImage(att.url) ? (
                        <Image
                            key={att._id || att.url}
                            src={att.url}
                            width={88}
                            height={66}
                            className={styles.commentAttachmentThumb}
                            placeholder
                        />
                    ) : (
                        <a
                            key={att._id || att.url}
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.commentAttachmentFile}
                        >
                            <FileOutlined />
                            <span>{att?.fileName || 'Download'}</span>
                        </a>
                    )
                )}
            </div>
        </div>
    )
}

const CommentList = ({
    comments: commentsProp,
    loading: loadingProp,
    embedded = false,
    emptyMessage = 'No previous results recorded for this test case.',
} = {}) => {
    const {
        testRunTestCaseDetailLoading = false,
        testRunTestCaseDetail: testCaseDetailData = {},
    } = useSelector(testRunState)

    const comments =
        commentsProp !== undefined
            ? commentsProp || []
            : testCaseDetailData?.comments || []
    const loading =
        loadingProp !== undefined
            ? loadingProp
            : testRunTestCaseDetailLoading

    if (loading) {
        return <SkeletonCommentHistory />
    }

    return (
        <div
            className={
                embedded
                    ? styles.commentHistoryEmbedded
                    : styles.commentHistoryPanel
            }
        >
            {!embedded && (
                <div className={styles.commentHistoryHeader}>
                    <div>
                        <p className={styles.commentHistoryEyebrow}>Activity</p>
                        <h3 className={styles.commentHistoryTitle}>
                            Comment history
                        </h3>
                    </div>
                    {isValidArray(comments) && (
                        <span className={styles.commentHistoryCount}>
                            {comments.length}
                        </span>
                    )}
                </div>
            )}

            {isValidArray(comments) ? (
                <ul className={styles.commentTimeline}>
                    {comments.map((c) => (
                        <li
                            key={c._id || c.date}
                            className={styles.commentItem}
                        >
                            <div className={styles.commentItemRail}>
                                <span className={styles.commentItemDot} />
                            </div>
                            <div className={styles.commentItemCard}>
                                <div className={styles.commentItemMeta}>
                                    <span className={styles.commentItemLabel}>
                                        Comment
                                    </span>
                                    <time
                                        className={styles.commentItemDate}
                                        title={fDateTime(c.date)}
                                    >
                                        {fDateTime(c.date)}
                                    </time>
                                </div>
                                {c.description ? (
                                    <p className={styles.commentItemBody}>
                                        {c.description}
                                    </p>
                                ) : null}
                                <AttachmentList attachments={c.attachments} />
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className={styles.commentHistoryEmpty}>{emptyMessage}</div>
            )}
        </div>
    )
}

export default CommentList
